import React, { useContext, useEffect, useState, useRef } from "react";
import {
    Button,
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalSection,
    StandardModalSectionHeader,
    StandardModalSectionTitle,
    Toast,
    ImageCropModal,
    VideoCropModal
} from "src/components";
import { TextLabel, RemoveMediaButton } from "../style";
import ContentForm from "../forms/ContentForm";
import UploadMedia from "../forms/UploadMedia";
import { BodyAndMindContext } from "../../BodyAndMindContext";
import { deleteContent, s3PresignedContentUrl, updateContentWithImage, updateContentWithVideo, saveAttachments } from "../../api";
import { LoadingIndicatorButton } from "src/scenes/Survey/components/Utils";
import ConfirmDialog from "../ConfirmDialog";
import CloseIcon from "src/img/CloseButton.png";
import { isBase64Image, isBase64Video } from "../utils/VideoUtils";
import { captionToHtml, htmlToCaption } from "src/components/RichTextEditor/htmlToCaption";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";
import { emptyContentHtml } from "src/components/RichTextEditor/adapters";
import Attachment from "./Attachment";
import { DirectorySelection } from "./DirectorySelection";
import BodyAndMindCheckInSection from "./BodyAndMindCheckInSection";
import { UserContext } from "src/scenes/App/UserContext";
import { FEATURE_FLAGS, hasFeature } from "src/constants/FeatureFlag";
import {
    DIRECTORY_LEVEL,
    getDirectoryLevel,
    getParentSubcategoryUuid
} from "../../directoryLevels";
import {
    hasValidCheckIn,
    normaliseCheckInForRequest
} from "src/components/CheckIn/checkIn";

function EditContent(props) {
    const uploadMediaRef = useRef(null);
    const {
        subcategoriesList,
    } = useContext(BodyAndMindContext);
    const user = useContext(UserContext);
    const canUseCheckedIn = hasFeature(user, FEATURE_FLAGS.CHECKED_IN);

    const { toggleEditContentModal, selectedContent, companyCategoriesList } = props;
    const selectedCheckIn = selectedContent?.checkIn || selectedContent?.check_in || null;
    const contentFileUuid = selectedContent?.image ? selectedContent?.image?.uuid : selectedContent?.video?.uuid;
    const originalFileUrl = selectedContent?.image?.imageUrl || selectedContent?.video?.videoUrl;

    const [title, setTitle] = useState(selectedContent?.title);
    const [htmlDescription, setHtmlDescription] = useState(selectedContent?.richTextDescription || captionToHtml(selectedContent?.description || ""));
    const [uploadFile, setUploadFile] = useState(selectedContent?.image?.imageUrl || selectedContent?.video?.videoUrl);
    const [selectedCategory, setSelectedCategory] = useState(selectedContent?.category.uuid);
    const [selectedSubcategory, setSelectedSubcategory] = useState(selectedContent?.subcategory.uuid);
    const [selectedTopic, setSelectedTopic] = useState("topic");
    const [selectedSubtopic, setSelectedSubtopic] = useState("subtopic");
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [dimensions, setDimensions] = useState(
        selectedContent?.image ?
            { width: selectedContent?.image?.width, height: selectedContent?.image?.height } :
            { width: null, height: null }
    );
    const [fileType, setFileType] = useState(selectedContent?.image ? "image" : "video");
    const [contentHasFile, setContentHasFile] = useState(selectedContent?.image || selectedContent?.video ? true : false);
    const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
    const [deleteIsLoading, setDeleteIsLoading] = useState(false);
    const [base64ImageToCrop, setBase64ImageToCrop] = useState(null);
    const [base64VideoFileToCrop, setBase64VideoFileToCrop] = useState(null);
    const [useCrop, setUseCrop] = useState([]);

    // Crop Image
    const [croppedBlob, setCroppedBlob] = useState(null);
    const [croppedVideo, setCroppedVideo] = useState(null);
    const [showCropperModal, setShowCropperModal] = useState(false);

    const [uploadedAttachmentFiles, setUploadedAttachmentFiles] = useState([]);
    const [deleteAttachmentFiles, setDeleteAttachmentFiles] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState(
        selectedContent?.attachments || []
    );
    const [checkIn, setCheckIn] = useState(selectedCheckIn);

    const onImageCropComplete = (file, width, height) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        if (uploadFile) {
            URL.revokeObjectURL(uploadFile);
        }

        setUploadFile(url); // For preview
        setDimensions({ width, height });
        setCroppedBlob(file);
        setShowCropperModal(false);
    };

    const onVideoCropComplete = (file, crop, trimStart, trimEnd, aspectRatioLabel) => {
        if (!file) {
            return;
        }
        setCroppedVideo(file);
        setBase64ImageToCrop(null);
        setBase64VideoFileToCrop(null);
        setUseCrop((prev) => ({
            ...prev,
            trimStart,
            trimEnd,
            aspectRatio: aspectRatioLabel,
            crop: crop,
        }));
    };

    useEffect(() => {
        const selectedDirectoryUuid = selectedContent?.subcategory?.uuid;
        const selectedDirectory = subcategoriesList.find(item => item.uuid === selectedDirectoryUuid);
        if (!selectedDirectory) return;

        const directoryLevel = getDirectoryLevel(selectedDirectory);
        const parentUuid = getParentSubcategoryUuid(selectedDirectory);
        if (directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY) {
            setSelectedSubcategory(selectedDirectory.uuid);
            setSelectedTopic("topic");
            setSelectedSubtopic("subtopic");
            return;
        }

        if (directoryLevel === DIRECTORY_LEVEL.TOPIC) {
            setSelectedSubcategory(parentUuid || "subcategory");
            setSelectedTopic(selectedDirectory.uuid);
            setSelectedSubtopic("subtopic");
            return;
        }

        const parentTopic = subcategoriesList.find(item => item.uuid === parentUuid);
        setSelectedSubcategory(parentTopic ? getParentSubcategoryUuid(parentTopic) : "subcategory");
        setSelectedTopic(parentUuid || "topic");
        setSelectedSubtopic(selectedDirectory.uuid);
    }, [selectedContent, subcategoriesList]);

    const setOnUploadFileChange = (file) => {
        setUploadFile(file);
        setErrors({ ...errors, uploadFile: "" });
        setContentHasFile(true);
        setBase64VideoFileToCrop(null);
        setBase64ImageToCrop(null);
        setCroppedBlob(null);

        if (isBase64Image(file)) {
            setBase64ImageToCrop(file);
            setShowCropperModal(true);
        }
        if (isBase64Video(file)) {
            setBase64VideoFileToCrop(file);
            setShowCropperModal(true);
        }
    };

    const onCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
        setSelectedSubcategory("subcategory");
        setSelectedTopic("topic");
        setSelectedSubtopic("subtopic");
    };

    const onSubcategoryChange = (e) => {
        setSelectedSubcategory(e.target.value);
        setSelectedTopic("topic");
        setSelectedSubtopic("subtopic");
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const onTopicChange = (e) => {
        setSelectedTopic(e.target.value);
        setSelectedSubtopic("subtopic");
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const onSubtopicChange = (e) => {
        setSelectedSubtopic(e.target.value);
        setErrors({ ...errors, subcategory: "" });
    };

    const getSelectedDirectoryUuid = () => {
        if (selectedSubtopic !== "subtopic") return selectedSubtopic;
        if (selectedTopic !== "topic") return selectedTopic;
        return selectedSubcategory;
    };

    const onTitleChange = (e) => {
        setTitle(e.target.value);
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const onHtmlDescriptionChange = (e) => {
        setHtmlDescription(e);
        setErrors({ ...errors, description: "" });
    };

    const toggleConfirmDeleteDialog = () => setShowConfirmDeleteDialog(!showConfirmDeleteDialog);

    // Manual validation
    const validate = () => {
        const newErrors = {};
        if (!title.trim()) newErrors.title = "Title is required";
        if (htmlDescription === emptyContentHtml) newErrors.description = "Description is required";
        if (!getSelectedDirectoryUuid().trim() || selectedSubcategory === "subcategory") newErrors.subcategory = "Sub-category is required";
        if (!uploadFile) newErrors.uploadFile = "Image or video file is required";
        if (canUseCheckedIn && checkIn && !hasValidCheckIn(checkIn)) newErrors.checkIn = "Select a location to finish this Check In, or remove it.";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsLoading(true);

        const payload = {
            subcategoryUuid: getSelectedDirectoryUuid(),
            title,
            description: htmlToCaption(htmlDescription),
            richTextDescription: htmlDescription,
            isFreeContent: 0,
            isWatchContent: 0,
            uuid: selectedContent?.uuid
        };
        if (canUseCheckedIn) {
            payload.checkIn = checkIn ? normaliseCheckInForRequest(checkIn) : null;
        }

        let uploadedAttachments = [];
        if (uploadedAttachmentFiles.length > 0) {
            try {
                uploadedAttachments = await Promise.all(
                    uploadedAttachmentFiles.map(async (attachment) => {
                        const fileExtension = attachment.file.type.split("/").pop();
                        const response = await s3PresignedContentUrl(
                            attachment.file,
                            fileExtension,
                            "attachment"
                        );
                        return {
                            uuid: response.uuid,
                            fileName: attachment.name,
                            fileUrl: response.storedUrl.split("?")[0],
                            fileType: attachment.type,
                        };
                    })
                );
            } catch {
                Toast.error("Failed to upload attachments.");
                setIsLoading(false);
                return;
            }
        }

        const filteredExistingAttachments = (existingAttachments || []).filter(
            file => !deleteAttachmentFiles.includes(file.uuid)
        );

        const finalAttachments = [
            ...filteredExistingAttachments.map(file => ({
                uuid: file.uuid,
                fileName: file.fileName,
                fileUrl: file.fileUrl,
                fileType: file.fileType
            })),
            ...uploadedAttachments
        ];

        try {
            if (!contentHasFile) {
                Toast.error("No media file found.");
                return;
            }

            let savedContent;
            // IMAGE HANDLING
            if (fileType === "image") {
                let imagePayload = {
                    uuid: contentFileUuid,
                    imageUrl: selectedContent?.image?.imageUrl,
                    width: dimensions.width,
                    height: dimensions.height,
                };

                if (uploadFile && croppedBlob) {
                    const image = croppedBlob;
                    const fileExtension = image.type.split("/").pop();

                    const response = await s3PresignedContentUrl(image, fileExtension, "image");
                    const imageUrl = response?.storedUrl.split("?")[0];

                    imagePayload = {
                        uuid: response.uuid,
                        imageUrl,
                        width: dimensions.width,
                        height: dimensions.height,
                    };
                }

                payload.image = imagePayload;

                savedContent = await updateContentWithImage(selectedContent.uuid, payload);

                const contentUuid = savedContent?.data?.updatedPost.uuid;
                if (contentUuid) {
                    try {
                        await saveAttachments(contentUuid, finalAttachments);
                    } catch (error) {
                        console.log("Error saving attachments:", error);
                        Toast.error("Attachments failed to save.");
                    }
                }

                if (savedContent) {
                    Toast.success("Image content updated.");
                    toggleEditContentModal();
                }
            }

            // VIDEO
            if (fileType === "video") {
                let videoPayload;

                if (uploadFile && croppedVideo) {
                    const fileExtension = croppedVideo.type.split("/").pop();
                    const videoRes = await s3PresignedContentUrl(croppedVideo, fileExtension, "video");
                    const videoUrl = videoRes?.storedUrl.split("?")[0];

                    videoPayload = {
                        uuid: videoRes.uuid,
                        previewImageUrl: "",
                        videoUrl,
                        aspectRatio: useCrop?.aspectRatio || "widescreen",
                    };
                } else {
                    videoPayload = {
                        uuid: selectedContent?.video?.videoKey,
                        previewImageUrl: selectedContent?.video?.previewImageUrl,
                        videoUrl: selectedContent?.video?.videoUrl,
                        aspectRatio: "widescreen",
                    };
                }

                payload.crop = {
                    x: useCrop?.crop?.croppedAreaPixels?.x ?? 0,
                    y: useCrop?.crop?.croppedAreaPixels?.y ?? 0,
                    width: useCrop?.crop?.croppedAreaPixels?.width ?? 0,
                    height: useCrop?.crop?.croppedAreaPixels?.height ?? 0,
                };
                payload.trimStart = useCrop?.trimStart ?? 0;
                payload.trimEnd = useCrop?.trimEnd ?? 0;

                payload.video = videoPayload;

                savedContent = await updateContentWithVideo(selectedContent.uuid, payload);

                const contentUuid = savedContent?.updatedPost.uuid;
                if (contentUuid) {
                    try {
                        await saveAttachments(contentUuid, finalAttachments);
                    } catch (error) {
                        console.log("Error saving attachments:", error);
                        Toast.error("Attachments failed to save.");
                    }
                }

                if (savedContent) {
                    Toast.success("Video content updated.");
                    toggleEditContentModal();
                }
                return;
            }
        } catch (error) {
            console.error("Error:", error);
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsLoading(false);
        }
    };


    const onConfirmDelete = async () => {
        setDeleteIsLoading(true);
        try {
            const response = await deleteContent(selectedContent.uuid);
            if (response) {
                Toast.success("Content deleted successfully.");
                toggleEditContentModal();
            }
        } catch (error) {
            console.error("Error deleting content:", error);
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setDeleteIsLoading(false);
        }
    };

    const handleRemoveVideo = (e) => {
        e?.stopPropagation();
        setUploadFile(null);
        setBase64VideoFileToCrop(null);
        setBase64ImageToCrop(null);
        setCroppedBlob(null);
        setUseCrop({});
        uploadMediaRef.current?.triggerFilePicker();
    };

    return (
        <>
            <StandardModal
                open={true}
                onClose={toggleEditContentModal}
                widthMode="wide"
                title="Edit Content"
                description="Update the title, description, media, attachments, and location for this content."
                bodyPadding="1.2rem 1.6rem 2rem"
                footer={(
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "0.75rem" }}>
                        <Button type="button" onClick={toggleConfirmDeleteDialog} border="1px solid #FF3B3B" color="#FF3B3B" width="140px" height="38px" style={{ borderRadius: "7px", boxShadow: "none" }}>Delete Content</Button>
                        <StandardModalActionGroup $pushRight>
                            <Button disabled={isLoading} type="button" onClick={toggleEditContentModal} border="1px solid #000" color="#000" width="140px" height="38px" style={{ borderRadius: "7px", boxShadow: "none" }}>Cancel</Button>
                            {isLoading ? <LoadingIndicatorButton><LottieLoadingIndicator height="2rem" width="2rem" margin="5px" containerHeight="2rem" /></LoadingIndicatorButton> : <Button type="submit" form="body-and-mind-edit-content-form" backgroundColor="#171A22" border="1px solid #171A22" color="#fff" width="140px" height="38px" style={{ borderRadius: "7px", boxShadow: "none" }}>Save</Button>}
                        </StandardModalActionGroup>
                    </div>
                )}
            >
                <form id="body-and-mind-edit-content-form" onSubmit={handleSubmit} style={{ display: "grid", gap: "1rem", minWidth: 0 }}>
                    <StandardModalSection>
                        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 21rem)", gap: "1rem", alignItems: "start" }}>
                            <ContentForm
                                title={title}
                                onTitleChange={onTitleChange}
                                htmlDescription={htmlDescription}
                                setHtmlDescription={onHtmlDescriptionChange}
                                errors={errors}
                            />
                            <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: "10px", width: "calc(100% - 1rem)", maxWidth: "19rem" }}>
                                <TextLabel>
                                    Upload Image/Video
                                </TextLabel>
                                <div style={{ position: "relative", display: "inline-block", width: "fit-content" }}>
                                    <UploadMedia
                                        ref={uploadMediaRef}
                                        onFileSelect={setOnUploadFileChange}
                                        file={uploadFile}
                                        allFileType={true}
                                        setDimensions={setDimensions}
                                        fileType={fileType}
                                        setFileType={setFileType}
                                        cropperResults={useCrop}
                                        boxStyle={{ width: "100%", height: "auto", aspectRatio: "16 / 9", containerWidth: "100%", borderRadius: "8px" }}
                                    />
                                    {uploadFile && (
                                        <RemoveMediaButton type="button" onClick={handleRemoveVideo} style={{ top: "-8px" }}>
                                            <img src={CloseIcon} alt="close button" width={24} height={24} />
                                        </RemoveMediaButton>
                                    )}
                                </div>
                                <TextLabel color="red" style={{ marginTop: "10px" }}>{errors.uploadFile}</TextLabel>
                            </div>
                        </div>
                    </StandardModalSection>

                    <StandardModalSection>
                        <Attachment
                            uploadedAttachmentFiles={uploadedAttachmentFiles}
                            setUploadedAttachmentFiles={setUploadedAttachmentFiles}
                            setDeleteAttachmentFiles={setDeleteAttachmentFiles}
                            existingAttachments={existingAttachments}
                            setExistingAttachments={setExistingAttachments}
                        />
                    </StandardModalSection>

                    {canUseCheckedIn && (
                        <BodyAndMindCheckInSection
                            value={checkIn}
                            onChange={(nextCheckIn) => {
                                setCheckIn(nextCheckIn);
                                setErrors(current => ({ ...current, checkIn: "" }));
                            }}
                            error={errors.checkIn}
                        />
                    )}

                    <StandardModalSection>
                        <StandardModalSectionHeader>
                            <div>
                                <StandardModalSectionTitle>Select Content Location</StandardModalSectionTitle>
                                <StandardModalDescription>Choose the category first, then narrow the location as needed.</StandardModalDescription>
                            </div>
                        </StandardModalSectionHeader>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "1rem" }}>
                            <DirectorySelection
                                categories={companyCategoriesList}
                                directories={subcategoriesList}
                                selectedCategory={selectedCategory}
                                selectedSubcategory={selectedSubcategory}
                                selectedTopic={selectedTopic}
                                selectedSubtopic={selectedSubtopic}
                                onCategoryChange={onCategoryChange}
                                onSubcategoryChange={onSubcategoryChange}
                                onTopicChange={onTopicChange}
                                onSubtopicChange={onSubtopicChange}
                                error={errors.subcategory}
                            />
                        </div>
                    </StandardModalSection>
                    {base64ImageToCrop && (
                        <ImageCropModal
                            imageSrc={base64ImageToCrop}
                            isOpen={showCropperModal}
                            onClose={(shouldClear) => {
                                setShowCropperModal(false);
                                if (shouldClear) {
                                    setUploadFile(originalFileUrl);
                                    setBase64ImageToCrop(null);
                                    setFileType(selectedContent?.image ? "image" : "video");
                                }
                            }}
                            onCropComplete={onImageCropComplete}
                            aspectRatios={[
                                { width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN },
                            ]}
                            allowedOrientations={["landscape"]}
                            lockedAspectRatio={undefined}
                        />
                    )}
                    {base64VideoFileToCrop && (
                        <VideoCropModal
                            videoSrc={base64VideoFileToCrop}
                            isOpen={true}
                            onClose={(shouldClear) => {
                                setBase64VideoFileToCrop(null);
                                if (shouldClear) {
                                    handleRemoveVideo();
                                }
                            }}
                            onCropComplete={(file, crop, trimStart, trimEnd, aspectRatioLabel) =>
                                onVideoCropComplete(file, crop, trimStart, trimEnd, aspectRatioLabel)
                            }
                            aspectRatios={[
                                { width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN },
                            ]}
                            allowedOrientations={["landscape"]}
                            lockedAspectRatio={undefined}
                        />
                    )}
                </form>
            </StandardModal>
            {showConfirmDeleteDialog && (
                <StandardModal
                    open={showConfirmDeleteDialog}
                    onClose={toggleConfirmDeleteDialog}
                    bodyPadding="0"
                    hideCloseButton
                >
                    <ConfirmDialog
                        title={"content"}
                        toggleModal={toggleConfirmDeleteDialog}
                        deleteFunction={onConfirmDelete}
                        isLoading={deleteIsLoading}
                    />
                </StandardModal>
            )}
        </>
    );
}

export default EditContent;
